/** Family data stays in memory. Never substitute patient search/registration for a verified link. */
export interface FamilyProfile {
  id: string;
  fullName: string;
  isPrimary: boolean;
  canActivate: boolean;
  canRemove: boolean;
}

export interface FamilyProfilesState {
  profiles: FamilyProfile[];
  activeProfileId: string;
  canAdd: boolean;
}

export interface NewFamilyMember {
  firstName: string;
  familyName: string;
  dateOfBirth: string; // ISO date, without timezone conversion
  streetAndNumber: string;
  zipCode: string;
  town: string;
  country: string;
  relationship: 'daughter' | 'son' | 'other';
  relationshipDetails?: string;
}

/**
 * Contract for a future authenticated backend adapter; there is no supported API yet.
 * Permissions and profiles must come from the server, never from name/date matching.
 * activateProfile must complete the authorized data-context handover (including clearing
 * old queries, drafts and rendered patient data) BEFORE resolving.
 * removeMember removes ONLY the link. For an active member it must also complete the
 * handover to the primary profile before resolving. Rejection preserves the link.
 */
export interface FamilyManagementService {
  readonly unavailableReason?: string;
  loadProfiles(signal: AbortSignal): Promise<FamilyProfilesState>;
  addMember(input: NewFamilyMember): Promise<FamilyProfile>;
  activateProfile(profileId: string): Promise<void>;
  removeMember(profileId: string): Promise<void>;
}

export const FAMILY_UNAVAILABLE =
  'Die Familienverwaltung ist derzeit noch nicht verfügbar. Bitte wenden Sie sich für eine sichere Freigabe an die Praxis.';

const unavailable = async (): Promise<never> => { throw new Error(FAMILY_UNAVAILABLE); };

// Deliberately no guessed endpoints, registration requests or localStorage fallback.
export const familyManagementService: FamilyManagementService = {
  unavailableReason: FAMILY_UNAVAILABLE,
  loadProfiles: unavailable,
  addMember: unavailable,
  activateProfile: unavailable,
  removeMember: unavailable,
};

export function parseFamilyBirthDate(value: string): string | null {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date.getFullYear() !== Number(year) || date.getMonth() !== Number(month) - 1 ||
      date.getDate() !== Number(day) || date > today) return null;
  return `${year}-${month}-${day}`;
}
